/**
 * claude
 *
 * Anthropic Claude API（Vision）を呼び出すラッパー。
 *
 * 重要：
 *   - ブラウザから直接APIを叩くため、`anthropic-dangerous-direct-browser-access: true`
 *     ヘッダーを付ける必要がある（公式仕様）。
 *   - APIキーはユーザー端末から直接送られる（v1の方針）。SaaS化時はサーバー経由に。
 *
 * 失敗時は CamelCase の `ClaudeApiError` を投げ、UI 側で日本語メッセージとして表示する。
 */

import { INTERPRET_PROMPT, getPromptForMode } from './prompts';
import type { VisionMode } from './prompts';
import { maskApiKey } from './storage';

import type { ClaudeContent, ClaudeResponse } from '../types';

/** モデルID（CLAUDE.md / REQUIREMENTS.md 準拠） */
const MODEL = 'claude-sonnet-4-20250514';

/** Anthropic API エンドポイント */
const ENDPOINT = 'https://api.anthropic.com/v1/messages';

/** APIバージョン（Anthropic公式） */
const API_VERSION = '2023-06-01';

/** 最大出力トークン（翻訳・要約結果としては十分な量） */
const MAX_TOKENS = 1024;

/**
 * Claude API 専用の例外
 * UI でこれを catch して、err.userMessage をそのまま画面に出す
 */
export class ClaudeApiError extends Error {
  /** HTTP ステータス（ネットワーク段階で失敗した場合は null） */
  readonly status: number | null;
  /** ユーザー向け日本語メッセージ */
  readonly userMessage: string;

  constructor(message: string, options: { status: number | null; userMessage: string }) {
    super(message);
    this.name = 'ClaudeApiError';
    this.status = options.status;
    this.userMessage = options.userMessage;
  }
}

/**
 * HTTPステータスとレスポンス本文から、ユーザー向けの日本語メッセージを決定
 */
function statusToUserMessage(status: number, bodySnippet: string): string {
  switch (status) {
    case 400:
      return `リクエスト内容にエラーがあります（${bodySnippet || 'bad request'}）。`;
    case 401:
      return 'APIキーが無効です。設定画面から正しいキーを再設定してください。';
    case 403:
      return 'APIキーの権限が不足しています。Anthropic Consoleでキーの状態を確認してください。';
    case 429:
      return 'リクエスト数が制限に達しました。しばらく待ってから再試行してください。';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Claude APIが一時的に応答できない状態です。少し待ってから再試行してください。';
    default:
      return `Claude APIエラー（HTTP ${status}）。`;
  }
}

export interface AnalyzeImageArgs {
  /** ユーザーが入力したAPIキー */
  apiKey: string;
  /** base64エンコード済み画像（"data:..." プレフィックスなし） */
  imageBase64: string;
  /** 翻訳 / 要約（画像解析専用：interpret はここに来ない） */
  mode: VisionMode;
  /** 中断用シグナル（任意） */
  signal?: AbortSignal;
}

/**
 * v0.2 構造化レスポンス（プロンプトでJSON出力を指示）
 *
 * パース失敗時は全フィールド未定義を許容し、UI 側はテキストフォールバック
 * （rawText）に切り替えて表示する。
 */
export interface AnalyzedPayload {
  /** 構造化フィールド：画像から読み取った原文 / 被写体記述 */
  original?: string;
  /** 構造化フィールド：日本語の翻訳 / 要約 */
  japanese?: string;
  /** 構造化フィールド：元言語のISO 639-1コード（例: 'en'） */
  sourceLang?: string;
  /** 構造化フィールド：自己評価信頼度 0-100 */
  confidence?: number;
  /** Claudeから返ってきた生テキスト（パース失敗時のフォールバック表示用） */
  rawText: string;
}

/**
 * Claudeレスポンスから JSON を取り出してフィールド検証
 *
 * 厳密にJSONを期待するが、Claudeが文章前後を返したり code fence を
 * 付けてきても拾えるよう、最初の {...} 区間を抽出する逃げ道を入れる。
 */
function tryParseStructured(rawText: string): Omit<AnalyzedPayload, 'rawText'> | null {
  // 1) そのままパース
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    // 2) 最初の `{` 〜 最後の `}` を切り出して再挑戦
    const start = rawText.indexOf('{');
    const end = rawText.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) return null;
    try {
      parsed = JSON.parse(rawText.slice(start, end + 1));
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;

  const original = typeof obj.original === 'string' ? obj.original : undefined;
  const japanese = typeof obj.japanese === 'string' ? obj.japanese : undefined;
  const sourceLang = typeof obj.sourceLang === 'string' ? obj.sourceLang : undefined;
  const confidence =
    typeof obj.confidence === 'number' && Number.isFinite(obj.confidence)
      ? Math.max(0, Math.min(100, Math.round(obj.confidence)))
      : undefined;

  // 最低限 japanese か original のどちらかは必要
  if (!original && !japanese) return null;

  const result: Omit<AnalyzedPayload, 'rawText'> = {};
  if (original !== undefined) result.original = original;
  if (japanese !== undefined) result.japanese = japanese;
  if (sourceLang !== undefined) result.sourceLang = sourceLang;
  if (confidence !== undefined) result.confidence = confidence;
  return result;
}

/**
 * 画像を Claude API に送って構造化された解析結果を返す
 *
 * 戻り値の rawText は Claudeから返った生テキスト（互換維持に使う）。
 * original/japanese/sourceLang/confidence はパース成功時のみ埋まる。
 */
export async function analyzeImage(args: AnalyzeImageArgs): Promise<AnalyzedPayload> {
  const { apiKey, imageBase64, mode, signal } = args;

  const prompt = getPromptForMode(mode);

  // 画像 + プロンプト の content blocks
  const content: ClaudeContent[] = [
    {
      type: 'image',
      source: {
        type: 'base64',
        media_type: 'image/jpeg',
        data: imageBase64,
      },
    },
    {
      type: 'text',
      text: prompt,
    },
  ];

  // デバッグ用ログ：APIキーは必ずマスク
  console.debug('[claude] analyzeImage:', {
    mode,
    model: MODEL,
    apiKey: maskApiKey(apiKey),
    imageBytes: Math.round((imageBase64.length * 3) / 4),
  });

  // exactOptionalPropertyTypes: true 環境では signal: undefined を渡せないので、
  // 渡されなかった時はキー自体を含めない形で組み立てる
  const requestInit: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': API_VERSION,
      // ブラウザから直接呼ぶことをAnthropicに明示（公式仕様）
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: MAX_TOKENS,
      messages: [
        {
          role: 'user',
          content,
        },
      ],
    }),
  };
  if (signal) requestInit.signal = signal;

  let response: Response;
  try {
    response = await fetch(ENDPOINT, requestInit);
  } catch (err) {
    // ネットワーク到達失敗（オフライン、CORSブロックなど）
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ClaudeApiError('Aborted', {
        status: null,
        userMessage: '解析を中断しました。',
      });
    }
    console.error('[claude] network error:', err);
    throw new ClaudeApiError('Network error', {
      status: null,
      userMessage: 'ネットワークに接続できませんでした。通信状態を確認してください。',
    });
  }

  if (!response.ok) {
    // エラー本文も少し見たいので部分取得（ただしユーザーに丸出しにはしない）
    const text = await response.text().catch(() => '');
    const snippet = text.slice(0, 200);
    console.error('[claude] HTTP', response.status, snippet);
    throw new ClaudeApiError(`HTTP ${response.status}`, {
      status: response.status,
      userMessage: statusToUserMessage(response.status, snippet),
    });
  }

  const json = (await response.json()) as ClaudeResponse;

  // 想定: content[0].text に翻訳/要約結果が入っている
  const firstText = json.content?.find((c) => c.type === 'text')?.text;
  if (typeof firstText !== 'string' || firstText.length === 0) {
    throw new ClaudeApiError('Empty response', {
      status: response.status,
      userMessage: 'Claude APIから空のレスポンスが返りました。再試行してください。',
    });
  }

  const rawText = firstText.trim();
  const structured = tryParseStructured(rawText);

  if (!structured) {
    // パース失敗時は rawText だけ返す（UI側でテキストフォールバック表示）
    console.warn('[claude] JSON parse failed, falling back to plain text');
    return { rawText };
  }

  return { ...structured, rawText };
}

// ============================================================================
//  通訳モード（v0.3+）：英語音声テキスト → 日本語翻訳
// ============================================================================

/** 通訳モードのトークン上限（短い会話の翻訳に十分） */
const INTERPRET_MAX_TOKENS = 200;

/** 通訳モードの temperature（一貫性重視） */
const INTERPRET_TEMPERATURE = 0.3;

export interface TranslateSpeechArgs {
  /** APIキー */
  apiKey: string;
  /** 認識された英語テキスト1セグメント */
  text: string;
  /** 中断用シグナル（任意） */
  signal?: AbortSignal;
}

/**
 * 1セグメントの英語テキストを日本語に翻訳する。
 *
 * 画像系の analyzeImage と異なり、JSON ではなく純粋な日本語文字列を返してもらう。
 * Claude が前置きや code fence を付けてくる事故対策として、最初の改行ブロックは
 * 取り除いて返す（呼び出し側の表示崩れ防止）。
 */
export async function translateSpeech(args: TranslateSpeechArgs): Promise<string> {
  const { apiKey, text, signal } = args;

  console.debug('[claude] translateSpeech:', {
    apiKey: maskApiKey(apiKey),
    chars: text.length,
    preview: text.slice(0, 40),
  });

  const requestInit: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': API_VERSION,
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: INTERPRET_MAX_TOKENS,
      temperature: INTERPRET_TEMPERATURE,
      system: INTERPRET_PROMPT,
      messages: [
        {
          role: 'user',
          content: text,
        },
      ],
    }),
  };
  if (signal) requestInit.signal = signal;

  let response: Response;
  try {
    response = await fetch(ENDPOINT, requestInit);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ClaudeApiError('Aborted', {
        status: null,
        userMessage: '翻訳を中断しました。',
      });
    }
    console.error('[claude] translateSpeech network error:', err);
    throw new ClaudeApiError('Network error', {
      status: null,
      userMessage: 'ネットワークに接続できませんでした。通信状態を確認してください。',
    });
  }

  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    const snippet = errBody.slice(0, 200);
    console.error('[claude] translateSpeech HTTP', response.status, snippet);
    throw new ClaudeApiError(`HTTP ${response.status}`, {
      status: response.status,
      userMessage: statusToUserMessage(response.status, snippet),
    });
  }

  const json = (await response.json()) as ClaudeResponse;
  const firstText = json.content?.find((c) => c.type === 'text')?.text;
  if (typeof firstText !== 'string' || firstText.length === 0) {
    throw new ClaudeApiError('Empty response', {
      status: response.status,
      userMessage: 'Claude APIから空のレスポンスが返りました。',
    });
  }

  // Claudeが万一 ```japanese とか付けてきたら剥がす（防御的）
  return firstText.trim().replace(/^```[a-z]*\s*|\s*```$/g, '').trim();
}
